export const API_ROUTES = {
  auth: {
    login: "/api/auth/login",
    logout: "/api/auth/logout",
    register: "/api/auth/register",
    forgotPassword: "/api/auth/forgot-password",
    resetPassword: "/api/auth/reset-password",
    refresh: "/api/auth/refresh",
  },
  users: {
    me: "/api/users/me",
  },
  events: {
    list: "/api/events",
    byId: (eventId: number | string) => `/api/events/${eventId}`,
    archive: (eventId: number | string) => `/api/events/${eventId}/archive`,
    duplicate: (eventId: number | string) => `/api/events/${eventId}/duplicate`,
    dayWindows: (eventId: number | string) =>
      `/api/events/${eventId}/day-windows`,
    coverImage: (eventId: number | string) =>
      `/api/events/${eventId}/cover-image`,
    feedback: (eventId: number | string) => `/api/events/${eventId}/feedback`,
    contractReminders: (eventId: number | string) =>
      `/api/events/${eventId}/contract-reminders`,
    ticketing: (eventId: number | string) => `/api/events/${eventId}/ticketing`,
    ticketingAvailable: (eventId: number | string) =>
      `/api/events/${eventId}/ticketing/available`,
    ticketingLink: (eventId: number | string) =>
      `/api/events/${eventId}/ticketing/link`,
  },
  eventRoles: {
    list: "/api/event-roles",
    byEvent: (eventId: number | string) => `/api/events/${eventId}/event-roles`,
    typesByEvent: (eventId: number | string) =>
      `/api/events/${eventId}/event-role-types`,
    typesByOrg: (orgId: number | string) =>
      `/api/event-role-types?org_id=${orgId}`,
    createType: "/api/event-role-types",
    updateType: "/api/event-role-types",
    deleteType: "/api/event-role-types",
    duplicate: (roleId: number | string) =>
      `/api/event-roles/${roleId}/duplicate`,
  },
  finance: {
    referentials: (orgId: number | string) =>
      `/api/finance/referentials?org_id=${orgId}`,
    parties: "/api/finance/parties",
    partyById: (id: number | string) => `/api/finance/parties/${id}`,
    vatRates: "/api/finance/vat-rates",
    vatRateById: (id: number | string) => `/api/finance/vat-rates/${id}`,
    categories: "/api/finance/categories",
    categoryById: (id: number | string) => `/api/finance/categories/${id}`,
  },
  accounting: {
    transactions: (eventId: number | string, orgId: number | string) =>
      `/api/events/${eventId}/accounting/transactions?org_id=${orgId}`,
    createTransaction: (eventId: number | string) =>
      `/api/events/${eventId}/accounting/transactions`,
    transactionById: (
      eventId: number | string,
      transactionId: number | string,
    ) => `/api/events/${eventId}/accounting/transactions/${transactionId}`,
    result: (eventId: number | string, orgId: number | string) =>
      `/api/events/${eventId}/accounting/result?org_id=${orgId}`,
    settlement: (eventId: number | string, orgId: number | string) =>
      `/api/events/${eventId}/accounting/settlement?org_id=${orgId}`,
    parties: (eventId: number | string, orgId: number | string) =>
      `/api/events/${eventId}/accounting/parties?org_id=${orgId}`,
    createParty: (eventId: number | string) =>
      `/api/events/${eventId}/accounting/parties`,
    partyById: (eventId: number | string, partyId: number | string) =>
      `/api/events/${eventId}/accounting/parties/${partyId}`,
    exportPdf: (eventId: number | string, orgId: number | string) =>
      `/api/events/${eventId}/accounting/export?org_id=${orgId}`,
  },
  assignments: {
    list: "/api/assignments",
    byEvent: (eventId: number | string) => `/api/events/${eventId}/assignments`,
    vacated: (eventId: number | string) =>
      `/api/events/${eventId}/assignments/vacated`,
    byRole: (eventId: number | string, roleId: number | string) =>
      `/api/events/${eventId}/event-roles/${roleId}/assignments`,
  },
  volunteers: {
    list: "/api/volunteers",
    stats: (orgId: number | string) => `/api/volunteers/stats?org_id=${orgId}`,
    import: "/api/volunteers/import",
    byEvent: (eventId: number | string) => `/api/volunteers/events/${eventId}`,
    exportRemunerations: (eventId: number | string, orgId: number | string) =>
      `/api/volunteers/events/${eventId}/remunerations/export?org_id=${orgId}`,
    eventVolunteer: (eventId: number | string, volunteerId: number | string) =>
      `/api/volunteers/events/${eventId}/${volunteerId}`,
    eventVolunteerRegistration: (
      eventId: number | string,
      volunteerId: number | string,
    ) => `/api/volunteers/events/${eventId}/${volunteerId}/registration`,
    rolePreferences: (volunteerId: number | string) =>
      `/api/volunteers/${volunteerId}/role-preferences`,
    rolePreference: (
      volunteerId: number | string,
      eventRoleTypeId: number | string,
    ) => `/api/volunteers/${volunteerId}/role-preferences/${eventRoleTypeId}`,
  },
  guests: {
    byEvent: (eventId: number | string, orgId: number | string) =>
      `/api/guests/events/${eventId}?org_id=${orgId}`,
    create: (eventId: number | string) => `/api/guests/events/${eventId}`,
    update: (eventId: number | string, eventGuestId: number | string) =>
      `/api/guests/events/${eventId}/${eventGuestId}`,
    presence: (eventId: number | string, eventGuestId: number | string) =>
      `/api/guests/events/${eventId}/${eventGuestId}/presence`,
    remove: (
      eventId: number | string,
      eventGuestId: number | string,
      orgId: number | string,
    ) => `/api/guests/events/${eventId}/${eventGuestId}?org_id=${orgId}`,
    copy: (eventId: number | string) => `/api/guests/events/${eventId}/copy`,
    share: (eventId: number | string) => `/api/guests/events/${eventId}/share`,
    revokeShare: (eventId: number | string) =>
      `/api/guests/events/${eventId}/share/revoke`,
    exportPdf: (eventId: number | string, orgId: number | string) =>
      `/api/guests/events/${eventId}/export?org_id=${orgId}`,
  },
  organisations: {
    list: "/api/organisations",
    me: "/api/organisations/me",
    bySlug: (slug: string) => `/api/organisations/${slug}`,
    signature: (slug: string) => `/api/organisations/${slug}/signature`,
    members: (slug: string) => `/api/organisations/${slug}/members`,
    member: (slug: string, userId: number | string) =>
      `/api/organisations/${slug}/members/${userId}`,
    volunteerSignupLink: (slug: string) =>
      `/api/organisations/${slug}/volunteer-signup-link`,
    regenerateVolunteerSignupLink: (slug: string) =>
      `/api/organisations/${slug}/volunteer-signup-link/regenerate`,
    ticketingIntegrations: (slug: string) =>
      `/api/organisations/${slug}/ticketing/integrations`,
    ticketingIntegration: (slug: string, provider: string) =>
      `/api/organisations/${slug}/ticketing/integrations/${provider}`,
    ticketingIntegrationTest: (slug: string, provider: string) =>
      `/api/organisations/${slug}/ticketing/integrations/${provider}/test`,
  },
  publicOrganisations: {
    volunteerSignup: (token: string) =>
      `/api/public/organisations/signup/${token}`,
    volunteerSignupRegister: (token: string) =>
      `/api/public/organisations/signup/${token}/register`,
  },
  volunteerAuth: {
    login: "/api/public/volunteer-auth/login",
    refresh: "/api/public/volunteer-auth/refresh",
    logout: "/api/public/volunteer-auth/logout",
    forgotPassword: "/api/public/volunteer-auth/forgot-password",
    resetPassword: "/api/public/volunteer-auth/reset-password",
    me: "/api/public/volunteer-auth/me",
  },
  volunteerApp: {
    dashboard: "/api/volunteer/me/dashboard",
    updateMaxHours: "/api/volunteer/me/max-hours",
    updateProfile: "/api/volunteer/me/profile",
    profilePhoto: "/api/volunteer/me/profile-photo",
    confirmAssignment: (assignmentId: number | string) =>
      `/api/volunteer/assignments/${assignmentId}/confirm`,
    cancelAssignment: (assignmentId: number | string) =>
      `/api/volunteer/assignments/${assignmentId}/cancel`,
    events: "/api/volunteer/events",
    eventDetail: (eventId: number | string) =>
      `/api/volunteer/events/${eventId}`,
    registerForEvent: (eventId: number | string) =>
      `/api/volunteer/events/${eventId}/register`,
    eventFeedback: (eventId: number | string) =>
      `/api/volunteer/events/${eventId}/feedback`,
    roleTypes: "/api/volunteer/role-types",
    rolePreferences: "/api/volunteer/role-preferences",
    rolePreference: (roleTypeId: number | string) =>
      `/api/volunteer/role-preferences/${roleTypeId}`,
    eventDayWindows: (eventId: number | string) =>
      `/api/volunteer/events/${eventId}/day-windows`,
    eventAvailabilities: (eventId: number | string) =>
      `/api/volunteer/events/${eventId}/availabilities`,
    eventCoVolunteers: (eventId: number | string) =>
      `/api/volunteer/events/${eventId}/co-volunteers`,
    eventPreferredPartner: (eventId: number | string) =>
      `/api/volunteer/events/${eventId}/preferred-partner`,
    eventGuests: (eventId: number | string) =>
      `/api/volunteer/events/${eventId}/guests`,
    eventGuestPresence: (
      eventId: number | string,
      eventGuestId: number | string,
    ) => `/api/volunteer/events/${eventId}/guests/${eventGuestId}/presence`,
    contractDownload: (contractId: number | string) =>
      `/api/volunteer/contracts/${contractId}/download`,
  },
  invitations: {
    create: "/api/invitations",
    list: (orgId: number | string) => `/api/invitations?org_id=${orgId}`,
    accept: (token: string) => `/api/invitations/${token}/accept`,
    delete: (invitationId: number | string, orgId: number | string) =>
      `/api/invitations/${invitationId}?org_id=${orgId}`,
  },
  availabilities: {
    list: "/api/availabilities",
    create: "/api/availabilities",
    delete: "/api/availabilities",
    byEvent: (eventId: number | string) =>
      `/api/availabilities/events/${eventId}`,
  },
  planning: {
    generate: (eventId: number | string) =>
      `/api/events/${eventId}/planning/generate`,
    stats: (eventId: number | string) =>
      `/api/events/${eventId}/planning/stats`,
    config: (eventId: number | string) =>
      `/api/events/${eventId}/planning/config`,
    delete: (eventId: number | string) => `/api/events/${eventId}/planning`,
    publish: (eventId: number | string) =>
      `/api/events/${eventId}/planning/publish`,
    sendSchedule: (eventId: number | string) =>
      `/api/events/${eventId}/planning/send-schedule`,
    deleteAssignment: (
      eventId: number | string,
      assignmentId: number | string,
    ) => `/api/events/${eventId}/planning/assignments/${assignmentId}`,
    exportPdf: (eventId: number | string) =>
      `/api/events/${eventId}/planning/export`,
    exportVolunteerPdf: (
      eventId: number | string,
      volunteerId: number | string,
    ) => `/api/events/${eventId}/volunteers/${volunteerId}/planning/export`,
  },
  contractTemplates: {
    list: (orgId: number | string) => `/api/contract-templates?org_id=${orgId}`,
    create: "/api/contract-templates",
    byId: (templateId: number | string) =>
      `/api/contract-templates/${templateId}`,
    update: (templateId: number | string) =>
      `/api/contract-templates/${templateId}`,
    default: "/api/contract-templates/default",
  },
  contracts: {
    list: "/api/contracts",
    create: "/api/contracts",
    stats: "/api/contracts/stats",
    byId: (contractId: number | string) => `/api/contracts/${contractId}`,
    patch: (contractId: number | string) => `/api/contracts/${contractId}`,
    generate: (contractId: number | string) =>
      `/api/contracts/${contractId}/generate`,
    send: (contractId: number | string) => `/api/contracts/${contractId}/send`,
    download: (contractId: number | string) =>
      `/api/contracts/${contractId}/download`,
    generateAllForEvent: (eventId: number | string) =>
      `/api/events/${eventId}/contracts/generate`,
    generateAllForEventStream: (eventId: number | string) =>
      `/api/events/${eventId}/contracts/generate/stream`,
    sendAllForEvent: (eventId: number | string) =>
      `/api/events/${eventId}/contracts/send`,
    remindForEvent: (eventId: number | string) =>
      `/api/events/${eventId}/contracts/remind`,
  },
  cookieConsent: {
    log: "/api/public/cookie-consents",
  },
  publicGuestLists: {
    byToken: (token: string) => `/api/public/guest-lists/${token}`,
  },
};
